import { c as _c } from "react/compiler-runtime";
import { FormSelect, FormInput, RadioGroup, FieldErrorAndDescription, Hint } from '@systemeio/ui';
import { SenderTypeEnum, SmsTemplateCreateInterface } from 'modules/sms/types';
import { useTwilioPhoneNumbers } from 'modules/profile/integrations/twilio/hooks/use-twilio-phone-numbers';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { SmsTemplateErrorsInterface } from 'modules/sms/types/errors-interface';
import TransLoco from 'shared/components/trans-loco';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { useTwilioMessageServices } from 'modules/profile/integrations/twilio/hooks/use-twilio-message-services';
import { useMemo, useEffect } from 'react';
export type SmsSenderFields = Pick<SmsTemplateCreateInterface, 'senderType' | 'phoneNumber' | 'alphanumericSender' | 'messageServiceSid'>;
export type SmsSenderErrors = Pick<SmsTemplateErrorsInterface, 'phoneNumber' | 'alphanumericSender' | 'messageServiceSid'>;
interface SmsTemplateRadioGroupSenderTypeProps {
  values: SmsSenderFields;
  onChange: (values: SmsSenderFields) => void;
  errors: SmsSenderErrors;
  isFetching: boolean;
  isPreFetching: boolean;
  disabled?: boolean;
  onTwilioHasErrors?: (hasTwilioErrors: boolean) => void;
}
export const SmsTemplateRadioGroupSenderType = t0 => {
  const $ = _c(84);
  const {
    values,
    onChange,
    errors,
    isFetching,
    isPreFetching,
    disabled,
    onTwilioHasErrors
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    data: twilioPhoneNumbers,
    errorNotFound: twilioPhoneNumbersNotFoundError
  } = useTwilioPhoneNumbers();
  const {
    data: twilioMessageServices,
    errorNotFound: twilioMessageServicesNotFoundError
  } = useTwilioMessageServices();
  let t1;
  if ($[0] !== twilioPhoneNumbers || $[1] !== twilioPhoneNumbersNotFoundError) {
    t1 = !!twilioPhoneNumbersNotFoundError ? [] : twilioPhoneNumbers?.map(_temp);
    $[0] = twilioPhoneNumbers;
    $[1] = twilioPhoneNumbersNotFoundError;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const phoneNumbers = t1;
  let t2;
  if ($[3] !== twilioMessageServices || $[4] !== twilioMessageServicesNotFoundError) {
    t2 = !!twilioMessageServicesNotFoundError ? [] : twilioMessageServices?.map(_temp2);
    $[3] = twilioMessageServices;
    $[4] = twilioMessageServicesNotFoundError;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const messageServices = t2;
  const hasTwilioErrors = !!(twilioPhoneNumbersNotFoundError || twilioMessageServicesNotFoundError);
  let t3;
  if ($[6] !== onChange || $[7] !== values) {
    t3 = updates => {
      onChange({
        ...values,
        ...updates
      });
    };
    $[6] = onChange;
    $[7] = values;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleChange = t3;
  let t4;
  if ($[9] !== hasTwilioErrors || $[10] !== onTwilioHasErrors) {
    t4 = () => {
      onTwilioHasErrors?.(hasTwilioErrors);
    };
    $[9] = hasTwilioErrors;
    $[10] = onTwilioHasErrors;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== hasTwilioErrors) {
    t5 = [hasTwilioErrors];
    $[12] = hasTwilioErrors;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== t) {
    t6 = t("dashboard.sms.sender_phone_number");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = disabled || isFetching;
  let t8;
  if ($[16] !== handleChange) {
    t8 = val => handleChange({
      phoneNumber: val || null
    });
    $[16] = handleChange;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = !values.phoneNumber;
  let t10;
  if ($[18] !== errors.phoneNumber || $[19] !== isPreFetching || $[20] !== phoneNumbers || $[21] !== t7 || $[22] !== t8 || $[23] !== t9 || $[24] !== values.phoneNumber) {
    t10 = <FormSelect value={values.phoneNumber} data={phoneNumbers} isPreFetching={isPreFetching} disabled={t7} onChange={t8} error={errors.phoneNumber} withoutCloseIcon={t9} />;
    $[18] = errors.phoneNumber;
    $[19] = isPreFetching;
    $[20] = phoneNumbers;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = values.phoneNumber;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t6) {
    t11 = {
      id: SenderTypeEnum.PhoneNumber,
      caption: t6,
      content: t10
    };
    $[26] = t10;
    $[27] = t6;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t) {
    t12 = t("dashboard.sms.alphanumeric_sender");
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      a: <a target="_blank" rel="noreferrer" className="primary-link" href="https://www.twilio.com/docs/glossary/what-alphanumeric-sender-id" />
    };
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t) {
    t14 = <Hint label={<TransLoco t={t} i18nKey="dashboard.sms.alphanumeric_sender_tooltip" components={t13} />} />;
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t) {
    t15 = t("dashboard.sms.alphanumeric_sender_placeholder");
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = values.alphanumericSender || "";
  let t17;
  if ($[36] !== handleChange) {
    t17 = e_1 => handleChange({
      alphanumericSender: e_1.target.value || null
    });
    $[36] = handleChange;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const t18 = disabled || isFetching;
  let t19;
  if ($[38] !== errors.alphanumericSender || $[39] !== isPreFetching || $[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t18) {
    t19 = <div onMouseDown={_temp3} onKeyDown={_temp4}><FormInput placeholder={t15} value={t16} onChange={t17} isPreFetching={isPreFetching} disabled={t18} error={errors.alphanumericSender} /></div>;
    $[38] = errors.alphanumericSender;
    $[39] = isPreFetching;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t12 || $[46] !== t14 || $[47] !== t19) {
    t20 = {
      id: SenderTypeEnum.Alphanumeric,
      caption: t12,
      additionalInfo: t14,
      content: t19
    };
    $[45] = t12;
    $[46] = t14;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t) {
    t21 = t("dashboard.sms.messaging_services");
    $[49] = t;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      a: <a target="_blank" rel="noreferrer" className="primary-link" href="https://www.twilio.com/docs/messaging/services" />
    };
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t) {
    t23 = <Hint label={<TransLoco t={t} i18nKey="dashboard.sms.messaging_services_tooltip" components={t22} />} />;
    $[52] = t;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  const t24 = disabled || isFetching;
  let t25;
  if ($[54] !== handleChange) {
    t25 = val_0 => handleChange({
      messageServiceSid: val_0 || null
    });
    $[54] = handleChange;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const t26 = !values.messageServiceSid;
  let t27;
  if ($[56] !== errors.messageServiceSid || $[57] !== isPreFetching || $[58] !== messageServices || $[59] !== t24 || $[60] !== t25 || $[61] !== t26 || $[62] !== values.messageServiceSid) {
    t27 = <FormSelect value={values.messageServiceSid} data={messageServices} isPreFetching={isPreFetching} disabled={t24} onChange={t25} error={errors.messageServiceSid} withoutCloseIcon={t26} />;
    $[56] = errors.messageServiceSid;
    $[57] = isPreFetching;
    $[58] = messageServices;
    $[59] = t24;
    $[60] = t25;
    $[61] = t26;
    $[62] = values.messageServiceSid;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t21 || $[65] !== t23 || $[66] !== t27) {
    t28 = {
      id: SenderTypeEnum.MessageService,
      caption: t21,
      additionalInfo: t23,
      content: t27
    };
    $[64] = t21;
    $[65] = t23;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t11 || $[69] !== t20 || $[70] !== t28) {
    t29 = [t11, t20, t28];
    $[68] = t11;
    $[69] = t20;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== handleChange) {
    t30 = type => handleChange({
      senderType: type
    });
    $[72] = handleChange;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== t29 || $[75] !== t30 || $[76] !== values.senderType) {
    t31 = <RadioGroup optionsWrapperClassName="flex-col w-full items-stretch" tooltipClassName="max-w-full w-full" optionWrapperClassName="whitespace-normal" options={t29} onChange={t30} selected={values.senderType} />;
    $[74] = t29;
    $[75] = t30;
    $[76] = values.senderType;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== hasTwilioErrors || $[79] !== t) {
    t32 = hasTwilioErrors && <FieldErrorAndDescription className="mt-2" error={<TransLoco t={t} i18nKey="dashboard.sms.twilio_not_found" components={{
      a: <LinkWithoutPrefetch target="_blank" className="primary-link" href="/profile/integrations" />
    }} />} />;
    $[78] = hasTwilioErrors;
    $[79] = t;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t31 || $[82] !== t32) {
    t33 = <div>{t31}{t32}</div>;
    $[81] = t31;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  return t33;
};
function _temp(phoneNumber) {
  return {
    id: phoneNumber,
    caption: phoneNumber
  };
}
function _temp2(messageService) {
  return {
    id: messageService.sid,
    caption: messageService.name
  };
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}